import { BarChart3 } from "lucide-react"

import { RatingStars } from "@/components/community/rating-stars"

type RecipeRatingProps = {
  average: number
  count: number
  distribution: Record<1 | 2 | 3 | 4 | 5, number>
}

export function RecipeRating({
  average,
  count,
  distribution,
}: RecipeRatingProps) {
  const maxCount = Math.max(...Object.values(distribution), 1)

  return (
    <section className="rounded-[2rem] border border-neutral-200 bg-white p-6 shadow-sm sm:p-8">
      <div className="mb-6">
        <div className="eyebrow">Рейтинг рецепта</div>

        <h2 className="mt-4 text-2xl font-semibold text-neutral-950 sm:text-3xl">
          Як читачі оцінили рецепт
        </h2>

        <p className="mt-3 text-sm leading-7 text-neutral-600 sm:text-base">
          Оцінки допомагають швидше зрозуміти, наскільки рецепт сподобався іншим
          користувачам і чи був зручним у приготуванні вдома.
        </p>
      </div>

      {count > 0 ? (
        <div className="grid gap-6 lg:grid-cols-[220px_minmax(0,1fr)] lg:items-start">
          <div className="rounded-[1.5rem] border border-neutral-200 bg-neutral-50 p-5 text-center">
            <div className="text-4xl font-semibold tracking-tight text-neutral-950">
              {average.toFixed(1)}
            </div>

            <RatingStars
              value={average}
              size="lg"
              className="mt-3 justify-center"
            />

            <div className="mt-3 text-sm text-neutral-600">
              На основі {count} {count === 1 ? "відгуку" : "відгуків"}
            </div>
          </div>

          <div className="rounded-[1.5rem] border border-neutral-200 bg-white p-5">
            <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-neutral-950">
              <BarChart3 className="h-4 w-4 text-emerald-700" />
              Розподіл оцінок
            </div>

            <div className="space-y-3">
              {[5, 4, 3, 2, 1].map((rating) => {
                const value = distribution[rating as 1 | 2 | 3 | 4 | 5]
                const widthPercent = maxCount > 0 ? (value / maxCount) * 100 : 0

                return (
                  <div
                    key={rating}
                    className="grid grid-cols-[32px_minmax(0,1fr)_40px] items-center gap-3"
                  >
                    <div className="text-sm font-medium text-neutral-700">
                      {rating}
                    </div>

                    <div className="h-2.5 overflow-hidden rounded-full bg-neutral-100">
                      <div
                        className="h-full rounded-full bg-amber-400"
                        style={{ width: `${widthPercent}%` }}
                      />
                    </div>

                    <div className="text-right text-sm text-neutral-500">
                      {value}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-[1.5rem] border border-dashed border-neutral-300 bg-neutral-50 p-6 text-sm leading-7 text-neutral-600">
          Поки що для цього рецепта немає оцінок. Ви можете стати першим
          користувачем, який поділиться враженням після приготування.
        </div>
      )}
    </section>
  )
}